import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Checkbox, Input, Select, Space, Table, Tag, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, ExportOutlined, ImportOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  apiErrorMessage,
  COMPETENCY_PERMS as P,
  competencyKeys,
  useAssessmentCycles,
  useCompetencyAssessments,
  useCompetencyCan,
} from '@/hooks/useCompetency';
import { competencyService, type CompetencyAssessment } from '@/services/competency.service';
import { triggerDownload } from '@/reports/utils/exportTable';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { ASSESSMENT_TYPES, ASSESSMENT_TYPE_LABEL, COMPETENCY_BASE, GRADE_COLOR, GRADE_LABEL, STATUS_COLOR } from './competencyDisplay';

const AssessmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const seesAll = useCompetencyCan(P.assessmentView, P.assessmentClose, P.assessmentEvaluate);
  const canViewCycles = useCompetencyCan(P.assessmentView, P.assessmentClose);
  const canImport = useCompetencyCan(P.import);
  const canExport = canImport || seesAll;
  const [status, setStatus] = useState<string | undefined>(params.get('status') ?? undefined);
  const [type, setType] = useState<string>();
  const [cycleId, setCycleId] = useState<string | undefined>(params.get('cycle_id') ?? undefined);
  const [mine, setMine] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<'template' | 'export' | 'import' | null>(null);
  const { data = [], isLoading, refetch } = useCompetencyAssessments({
    status,
    type,
    cycle_id: cycleId,
    mine: mine ? '1' : undefined,
    search,
  });
  const { data: cycles = [] } = useAssessmentCycles(canViewCycles);

  const columns: ColumnsType<CompetencyAssessment> = [
    {
      title: 'Assessment',
      dataIndex: 'assessment_number',
      width: 150,
      render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/assessments/${r.id}`)}>{v}</a>,
    },
    { title: 'Employee Name', render: (_, r) => [r.employee_number, r.employee_name].filter(Boolean).join(' - ') },
    { title: 'Designation', dataIndex: 'job_title', ellipsis: true },
    { title: 'Department', dataIndex: 'department_name', ellipsis: true },
    {
      title: 'Type of Assessment',
      width: 200,
      render: (_, r) =>
        r.assessment_type === 'Other'
          ? r.assessment_type_other || ASSESSMENT_TYPE_LABEL.Other
          : ASSESSMENT_TYPE_LABEL[r.assessment_type] || r.assessment_type,
    },
    { title: 'Date of Assessment', dataIndex: 'assessment_date', width: 130, render: (v) => formatDate(v) },
    { title: 'Evaluator Name', dataIndex: 'evaluator_name', width: 160 },
    {
      title: 'Overall Grade',
      width: 170,
      render: (_, r) => (r.grade ? <Tag color={GRADE_COLOR[r.grade]}>{`${r.grade} (${r.total_score}) ${GRADE_LABEL[r.grade]}`}</Tag> : '-'),
    },
    { title: 'Recommendation', dataIndex: 'recommendation', width: 150 },
    {
      title: 'Status',
      width: 170,
      render: (_, r) => (
        <Space size={4}>
          <Tag color={STATUS_COLOR[r.status]}>{r.status}</Tag>
          {r.hr_outcome && <Tag>{r.hr_outcome}</Tag>}
        </Space>
      ),
    },
  ];

  const downloadTemplate = async () => {
    setBusy('template');
    try {
      triggerDownload(await competencyService.downloadImportTemplate('assessments'), 'competency-assessments-template.xlsx');
      message.success('Template downloaded');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const exportCurrent = async () => {
    setBusy('export');
    try {
      triggerDownload(await competencyService.exportAssessments(), 'competency-assessments-export.xlsx');
      message.success('Export started');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const importFile = async (file: File) => {
    setBusy('import');
    try {
      const check = await competencyService.runImport('assessments', file, true);
      if (check.errors.length) {
        message.error(
          `Check failed: ${check.errors[0].message}${check.errors.length > 1 ? ` (+${check.errors.length - 1} more)` : ''}`
        );
        navigate(`${COMPETENCY_BASE}/import?kind=assessments`);
        return false;
      }
      const result = await competencyService.runImport('assessments', file, false);
      message.success(`Imported: ${result.created} created, ${result.updated} updated`);
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      void refetch();
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
    return false;
  };

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title="Competency assessments"
        extra={
          <Space wrap>
            {canExport ? (
              <Button icon={<ExportOutlined />} loading={busy === 'export'} onClick={() => void exportCurrent()}>
                Export
              </Button>
            ) : null}
            {canImport ? (
              <>
                <Button icon={<DownloadOutlined />} loading={busy === 'template'} onClick={() => void downloadTemplate()}>
                  Template
                </Button>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={(f) => {
                    void importFile(f);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={busy === 'import'}>
                    Import
                  </Button>
                </Upload>
                <Link to={`${COMPETENCY_BASE}/import?kind=assessments`}>
                  <Button icon={<ImportOutlined />}>Bulk import wizard</Button>
                </Link>
              </>
            ) : null}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`${COMPETENCY_BASE}/assessments/new`)}>
              New assessment
            </Button>
          </Space>
        }
      />
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search placeholder="Search number or employee" allowClear style={{ width: 260 }} onSearch={setSearch} />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 140 }}
            value={status}
            onChange={setStatus}
            options={['Draft', 'Submitted', 'Closed'].map((s) => ({ value: s, label: s }))}
          />
          <Select
            allowClear
            placeholder="Type of Assessment"
            style={{ width: 260 }}
            value={type}
            onChange={setType}
            options={ASSESSMENT_TYPES.map((s) => ({ value: s, label: ASSESSMENT_TYPE_LABEL[s] || s }))}
          />
          {canViewCycles && (
            <Select
              allowClear
              placeholder="Yearly cycle"
              style={{ width: 150 }}
              value={cycleId}
              onChange={setCycleId}
              options={cycles.map((c) => ({ value: c.id, label: String(c.cycle_year) }))}
            />
          )}
          {seesAll && (
            <Checkbox checked={mine} onChange={(e) => setMine(e.target.checked)}>
              My team only
            </Checkbox>
          )}
        </Space>
        <Table rowKey="id" size="small" loading={isLoading} dataSource={data} columns={columns} pagination={{ defaultPageSize: 20 }} scroll={{ x: 1400 }} />
      </Card>
    </div>
  );
};

export default AssessmentsPage;
